import type {
  IPresignedUrlResponseModel,
  IPresignedUrlViewModel,
  IExtractedResultModel,
  IExtractedResultViewModel,
  IExtractedItemViewModel,
  IExtractedItemResponse,
  IExtractedNodeReponse,
  IExtractedNodeViewModel,
  IProcessItemModel,
  IProcessItemViewModel,
  IProcessInfoModel,
  IProcessInfoViewModel,
  IDisplayImageViewModel,
  IDisplayImageModel,
  INotificationMessage,
  INotificationMessageViewModel,
  IProcessItemDetailModel,
  IProcessItemDetailViewModel,
  IProcessDetailResponse,
  IProcessDetailViewModel,
  IProcessListResponse,
  IProcessListViewModel,
  UserInstructionResponse,
  UserInstructionViewModel,
} from "@models/extraction";

export const buildDisplayImageViewModel = (
  displayImage: IDisplayImageModel,
): IDisplayImageViewModel => ({
  pageNumber: displayImage.page_number,
  url: displayImage.url,
});

export const buildPresignedUrlViewModel = (
  response: IPresignedUrlResponseModel,
): IPresignedUrlViewModel => ({
  id: response.id,
  uploadUrl: response.upload_url,
  expiresIn: response.expires_in,
});

export const buildExtractedItemViewModel = (
  field: IExtractedItemResponse,
): IExtractedItemViewModel => ({
  id: field.id,
  key: field.key,
  value: field.value,
  page: field.page,
  originalPage: field.original_page ?? 0,
  uom: field.uom,
  bbox: field.bbox,
  conf: field.conf,
});

export const buildExtractedNodeViewModel = (
  response: IExtractedNodeReponse,
): IExtractedNodeViewModel => ({
  id: response.id,
  name: response.name,
  displayName: response.display_name,
  level: response.level,
  isExpandable: response.is_expandable,
  children: response.children.map(buildExtractedNodeViewModel),
  data: response.data ? buildExtractedItemViewModel(response.data) : null,
});

export const buildExtractedResultViewModel = (
  response: IExtractedResultModel,
): IExtractedResultViewModel => {
  const isPdf = response.is_pdf ?? false;
  const noPages = response.no_pages ?? 1;

  return {
    displayNode: response.display_nodes.map(buildExtractedNodeViewModel),
    errorMessage: response.error_message,
    previewUrl: response.preview_url,
    isPdf,
    noPages,
  };
};

export const buildProcessItemViewModel = (
  item: IProcessItemModel,
): IProcessItemViewModel => ({
  id: item.id,
  fileName: item.file_name,
  status: item.status,
  demoImage: item.demo_image,
  createdAt: item.created_at,
  errorMessage: item.error_message,
});

export const buildProcessedInfoViewModel = (
  response: IProcessInfoModel,
): IProcessInfoViewModel => ({
  id: response.id,
  status: response.status,
  createdAt: response.created_at,
  isBatch: response.is_batch,
  errorMessage: response.error_message,
  items: response.items.map(buildProcessItemViewModel),
  mappingEnabled: response.mapping_enabled ?? false,
});

export const buildProcessListViewModel = (
  response: IProcessListResponse,
): IProcessListViewModel => ({
  items: response.items.map(buildProcessedInfoViewModel),
  nextCursor: response.next_cursor,
  hasMore: response.has_more,
});

export const buildNotificationMessageViewModel = (
  message: INotificationMessage,
): INotificationMessageViewModel => ({
  type: message.type,
  message: message.message,
  status: message.status,
  batchItemId: message.batch_item_id,
  batchItemStatus: message.batch_item_status,
  processId: message.process_id,
  timestamp: message.timestamp,
});

export const buildProcessItemDetailViewModel = (
  item: IProcessItemDetailModel,
): IProcessItemDetailViewModel => ({
  id: item.id,
  fileName: item.file_name,
  status: item.status,
  errorMessage: item.error_message,
  createdAt: item.created_at,
});

export const buildProcessDetailViewModel = (
  response: IProcessDetailResponse,
): IProcessDetailViewModel => ({
  id: response.id,
  status: response.status,
  progress: {
    total: response.progress.total,
    completed: response.progress.completed,
  },
  isBatch: response.is_batch,
  startAt: response.start_at,
  finishedAt: response.finished_at,
  errorMessage: response.error_message,
  userInput: response.user_input,
  processItems: response.process_items.map(buildProcessItemDetailViewModel),
});

export const buildUserInstructionViewModel = (
  response: UserInstructionResponse,
): UserInstructionViewModel => ({
  id: response.id,
  name: response.name,
  instruction: response.instruction,
  doctypeCode: response.doctype_code,
  organizationId: response.organization_id,
  createdBy: response.created_by,
  createdAt: response.created_at,
});
